import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { googleLogout } from '@react-oauth/google';

const AuthContext = createContext(null);

const STORAGE_KEY = 'ccco_auth';
const ALLOWED_DOMAIN = 'craftedcapitalco.com';

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  const [error, setError] = useState(null);
  const [expiringSoon, setExpiringSoon] = useState(false);

  const signIn = useCallback((credentialResponse) => {
    setError(null);
    try {
      const token = credentialResponse.credential;
      if (!token) {
        setError('Sign-in failed. No credential received.');
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));

      const email = payload.email || '';
      const emailVerified = payload.email_verified;
      const hostedDomain = payload.hd;
      const name = payload.name || email;
      const picture = payload.picture || '';
      const exp = payload.exp ? payload.exp * 1000 : Date.now() + 60 * 60 * 1000;

      if (!emailVerified) {
        setError('Your Google email is not verified. Sign-in denied.');
        return;
      }

      const emailDomain = email.split('@')[1] || '';
      if (hostedDomain !== ALLOWED_DOMAIN && emailDomain !== ALLOWED_DOMAIN) {
        setError(`Access denied. Only @${ALLOWED_DOMAIN} accounts are allowed.`);
        return;
      }

      const newAuth = {
        token,
        email,
        name,
        picture,
        expiresAt: exp,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(newAuth));
      setAuth(newAuth);
    } catch (err) {
      setError('Sign-in failed. Please try again.');
    }
  }, []);

  const signOut = useCallback(() => {
    try {
      googleLogout();
    } catch {}
    localStorage.removeItem(STORAGE_KEY);
    setAuth(null);
    setError(null);
  }, []);

  // Google ID tokens last one hour and cannot be extended without a fresh sign-in.
  // Rather than dropping her without warning, warn ten minutes out so there is time
  // to save, and only sign out once the token is genuinely dead.
  useEffect(() => {
    if (!auth?.expiresAt) { setExpiringSoon(false); return; }
    const msUntilExpiry = auth.expiresAt - Date.now();
    if (msUntilExpiry <= 0) {
      signOut();
      return;
    }
    const WARN_AT = 10 * 60 * 1000;
    setExpiringSoon(msUntilExpiry <= WARN_AT);
    const timers = [];
    if (msUntilExpiry > WARN_AT) {
      timers.push(setTimeout(() => setExpiringSoon(true), msUntilExpiry - WARN_AT));
    }
    timers.push(setTimeout(() => { signOut(); }, msUntilExpiry));
    return () => timers.forEach(clearTimeout);
  }, [auth, signOut]);

  const value = {
    user: auth ? { email: auth.email, name: auth.name, picture: auth.picture } : null,
    token: auth?.token || null,
    isAuthenticated: !!auth,
    error,
    signIn,
    signOut,
    expiringSoon,
    minutesLeft: auth?.expiresAt ? Math.max(0, Math.round((auth.expiresAt - Date.now()) / 60000)) : 0,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export async function authFetch(url, options = {}) {
  let token = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (!parsed.expiresAt || parsed.expiresAt > Date.now()) {
        token = parsed.token;
      }
    }
  } catch {}

  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  return fetch(url, { ...options, headers });
}
